import React, { useState, useEffect } from 'react';
import { 
  Target, Compass, Zap, Lightbulb, BookOpen, MessageCircle,
  CheckCircle, TrendingUp, Award, ChevronRight, X, Sparkles, 
  FileText, HelpCircle, AlertCircle, Package, Brain
} from 'lucide-react';

// Fonction pour calculer les métriques d'une étape (logique de ResultsManager)
const calculateStepMetrics = (stepNotes) => {
  const metrics = {
    hasDecisions: false,
    hasQuestions: false,
    hasRecommendations: false,
    decisionsCount: 0,
    questionsCount: 0,
    recommendationsCount: 0,
    completionScore: 0
  };

  if (!stepNotes || typeof stepNotes !== 'object') {
    return metrics;
  }

  // Compter les décisions
  if (stepNotes.decisions && stepNotes.decisions.trim().length > 0) {
    metrics.hasDecisions = true;
    const sections = stepNotes.decisions.split('\n\n').filter(s => s.trim());
    metrics.decisionsCount = sections.length;
  }

  // Compter les questions
  if (stepNotes.questions && stepNotes.questions.trim().length > 0) {
    metrics.hasQuestions = true;
    const sections = stepNotes.questions.split('\n\n').filter(s => s.trim());
    metrics.questionsCount = sections.length;
  }

  // Compter les recommandations
  if (stepNotes.ai_recommendations && stepNotes.ai_recommendations.trim().length > 0) {
    metrics.hasRecommendations = true;
    const sections = stepNotes.ai_recommendations.split('\n\n').filter(s => s.trim());
    metrics.recommendationsCount = sections.length;
  }

  return metrics;
};

// Fonction d'analyse de l'état de l'objectif
const analyzeObjectiveState = (objective) => {
  const hasStrategy = objective.hasStrategy || objective.recommendation;
  const hasActionPlan = objective.hasActionPlan || objective.actionPlan;
  
  // Récupérer les sources
  let sourcesCount = 0;
  if (objective.savedSources && Array.isArray(objective.savedSources)) {
    sourcesCount = objective.savedSources.length;
  } else if (objective.inspirationSources && Array.isArray(objective.inspirationSources)) {
    sourcesCount = objective.inspirationSources.length;
  }
  
  const totalActions = objective.actionsCount || 0;
  const completedActions = objective.completedActionsCount || 0;
  const performance = objective.performance || 0;
  
  // Calculer les métriques précises avec la logique de ResultsManager
  const notesStats = {
    decisions: 0,
    challenges: 0,
    recommendations: 0,
    resources: 0,
    sources: sourcesCount,
    totalSteps: 0
  };

  // Parcourir toutes les notes par étape
  if (objective.stepNotes && typeof objective.stepNotes === 'object') {
    notesStats.totalSteps = Object.keys(objective.stepNotes).length;
    
    Object.values(objective.stepNotes).forEach(stepData => {
      const stepMetrics = calculateStepMetrics(stepData);
      notesStats.decisions += stepMetrics.decisionsCount;
      notesStats.challenges += stepMetrics.questionsCount;
      notesStats.recommendations += stepMetrics.recommendationsCount;
    });
  }
  
  // Déterminer la phase actuelle
  let currentPhase = 'ambition';
  if (hasActionPlan) {
    currentPhase = 'action';
  } else if (hasStrategy) {
    currentPhase = 'stratégie';
  }

  // Calculer la progression globale
  let globalProgress = performance;
  if (globalProgress === 0) {
    if (hasActionPlan) globalProgress = 40;
    else if (hasStrategy) globalProgress = 20;
    else globalProgress = 10;
  }

  // Déterminer le message de bienvenue et les alertes
  let welcomeMessage = {};
  let recommendations = [];
  let alerts = [];

  if (currentPhase === 'ambition') {
    welcomeMessage = {
      title: "Définissez votre stratégie",
      subtitle: "Votre objectif est bien défini",
      message: "Vous avez créé cet objectif mais n'avez pas encore exploré de stratégie. Je vous recommande de commencer par obtenir des recommandations personnalisées pour structurer votre approche."
    };
    
    recommendations = [
      {
        icon: Compass,
        title: "Obtenir une stratégie",
        description: "Recevez des recommandations personnalisées",
        reason: "→ Première étape pour transformer votre ambition en plan concret",
        target: 'strategy'
      },
      {
        icon: Lightbulb,
        title: "Rechercher de l'inspiration",
        description: "Explorez des idées et bonnes pratiques",
        target: 'inspiration'
      },
      {
        icon: MessageCircle,
        title: "Discuter avec Aladinn",
        description: "Affinez votre objectif par le dialogue",
        target: 'chat'
      }
    ];
  } else if (currentPhase === 'stratégie') {
    welcomeMessage = {
      title: "Passez à l'action !",
      subtitle: "Votre stratégie est prête",
      message: hasActionPlan 
        ? "Vous avez une stratégie et un début de plan d'action. Je vous recommande de poursuivre via le chat pour affiner vos actions et suivre votre avancement."
        : "Vous disposez de recommandations stratégiques. Il est temps de les transformer en plan d'action concret avec des étapes mesurables."
    };
    
    if (hasActionPlan) {
      recommendations = [
        {
          icon: MessageCircle,
          title: "Chat avec Aladinn",
          description: "Affinez votre plan et suivez vos actions",
          reason: "→ Meilleur moyen de progresser avec un accompagnement personnalisé",
          target: 'chat'
        },
        {
          icon: Zap,
          title: "Voir le plan d'action",
          description: `${completedActions}/${totalActions} actions complétées`,
          target: 'action'
        },
        {
          icon: BookOpen,
          title: "Enrichir ma base de connaissances",
          description: "Ajoutez des ressources utiles",
          target: 'exploration'
        }
      ];
    } else {
      recommendations = [
        {
          icon: Zap,
          title: "Créer un plan d'action",
          description: "Transformez votre stratégie en étapes concrètes",
          reason: "→ Vous avez la stratégie, passons maintenant à l'exécution",
          target: 'action'
        },
        {
          icon: MessageCircle,
          title: "Discuter avec Aladinn",
          description: "Affinez votre stratégie par le dialogue",
          target: 'chat'
        },
        {
          icon: Compass,
          title: "Revoir la stratégie",
          description: "Consultez vos recommandations",
          target: 'strategy'
        }
      ];
    }
  } else if (currentPhase === 'action') {
    const progressRate = totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0;
    const hasDecisions = notesStats.decisions > 0;
    const hasChallenges = notesStats.challenges > 0;
        
    if (hasDecisions && !hasChallenges && progressRate < 50) {
      alerts.push({
        icon: TrendingUp,
        iconColor: 'text-green-600',
        color: 'border-green-300 bg-green-50',
        title: 'Belle dynamique !',
        message: `${notesStats.decisions} décision(s) documentée(s). Continuez à avancer.`
      });
    }
    
    if (progressRate < 30) {
      welcomeMessage = {
        title: "Poursuivez votre projet !",
        subtitle: "Votre plan d'action est en cours",
        message: hasDecisions 
          ? `Vous avez documenté ${notesStats.decisions} décision(s) et réalisé ${completedActions}/${totalActions} actions. ${hasChallenges ? `Vous avez ${notesStats.challenges} question(s)/défi(s) en attente.` : ''} Le chat Aladinn est votre meilleur allié pour maintenir votre élan.`
          : `Votre plan d'action est lancé (${completedActions}/${totalActions} actions). Je vous recommande d'utiliser le chat pour suivre votre avancement et documenter vos décisions.`
      };
      
      recommendations = [
        {
          icon: MessageCircle,
          title: "Chat avec Aladinn",
          description: hasChallenges 
            ? `Résolvez vos ${notesStats.challenges} question(s)/défi(s)` 
            : "Suivez vos actions et documentez votre progression",
          reason: hasChallenges 
            ? "→ Débloquez vos points d'interrogation pour avancer" 
            : "→ L'accompagnement continu est clé pour maintenir votre dynamique",
          target: 'chat'
        },
        {
          icon: Zap,
          title: "Consulter le plan d'action",
          description: `${totalActions - completedActions} action(s) restante(s)`,
          target: 'action'
        },
        {
          icon: FileText,
          title: "Mes notes d'avancement",
          description: `${notesStats.decisions} décision(s) • ${notesStats.challenges} défi(s)`,
          target: 'chat'
        }
      ];
    } else if (progressRate < 75) {
      welcomeMessage = {
        title: "Excellente progression !",
        subtitle: "Vous êtes sur la bonne voie",
        message: `Vous avez complété ${completedActions} actions sur ${totalActions}. ${hasDecisions ? `Vos ${notesStats.decisions} décision(s) documentée(s) témoignent de votre engagement.` : ''} ${hasChallenges ? `${notesStats.challenges} question(s)/défi(s) nécessitent votre attention.` : ''} Continuez avec le chat pour maintenir votre rythme.`
      };
      
      recommendations = [
        {
          icon: MessageCircle,
          title: "Chat avec Aladinn",
          description: hasChallenges 
            ? `Adressez vos ${notesStats.challenges} défi(s) en attente`
            : "Continuez votre accompagnement personnalisé",
          reason: hasChallenges
            ? "→ Clarifiez vos questions pour accélérer votre progression"
            : "→ Gardez le cap avec un soutien régulier",
          target: 'chat'
        },
        {
          icon: Zap,
          title: "Actions restantes",
          description: `${totalActions - completedActions} action(s) à compléter`,
          target: 'action'
        },
        {
          icon: BookOpen,
          title: "Base de connaissances",
          description: notesStats.resources > 0 
            ? `${notesStats.resources} ressource(s) sauvegardée(s)` 
            : "Enrichissez vos ressources",
          target: 'exploration'
        }
      ];
    } else {
      welcomeMessage = {
        title: "Bientôt l'objectif atteint !",
        subtitle: "Dernière ligne droite",
        message: `Félicitations ! Vous avez complété ${completedActions}/${totalActions} actions (${progressRate}%). ${hasDecisions ? `Vos ${notesStats.decisions} décision(s) documentent votre parcours.` : ''} ${hasChallenges ? `Finalisez vos ${notesStats.challenges} dernier(s) défi(s) et` : ''} Partagez vos résultats !`
      };
      
      recommendations = [
        {
          icon: MessageCircle,
          title: "Finaliser avec Aladinn",
          description: hasChallenges 
            ? `Résolvez les ${notesStats.challenges} dernier(s) défi(s)`
            : "Complétez les dernières actions",
          reason: "→ La dernière ligne droite mérite un accompagnement",
          target: 'chat'
        },
        {
          icon: Award,
          title: "Partager vos résultats",
          description: "Célébrez votre réussite",
          target: 'share'
        },
        {
          icon: FileText,
          title: "Synthèse du projet",
          description: "Documentez vos apprentissages",
          target: 'chat'
        }
      ];
    }
  }

  // Accès rapides aux ressources avec compteurs précis
  const quickAccess = [
    {
      icon: Compass,
      label: "Stratégie",
      target: 'strategy',
      available: hasStrategy,
      count: 0
    },
    {
      icon: Zap,
      label: "Plan d'action",
      target: 'action',
      available: hasActionPlan,
      count: totalActions - completedActions
    },
    {
      icon: MessageCircle,
      label: "Exploration",
      target: 'exploration',
      available: true,
      count: notesStats.decisions + notesStats.challenges
    },
    {
      icon: Lightbulb,
      label: "Inspiration",
      target: 'inspiration',
      available: true,
      count: notesStats.sources
    }
  ];

  return {
    currentPhase,
    globalProgress,
    welcomeMessage,
    recommendations,
    alerts,
    notesStats,
    quickAccess
  };
};

// Composant principal
const SmartObjectiveWelcome = ({ objective, onNavigate, onClose }) => {
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (objective) {
      setAnalysis(analyzeObjectiveState(objective));
    }
  }, [objective]);

  if (!analysis) return null;

  // Couleurs mauves douces selon la phase
  const getPhaseStyle = () => {
    switch (analysis.currentPhase) {
      case 'ambition':
        return {
          gradient: 'from-purple-400 to-purple-600',
          bg: 'bg-purple-50',
          icon: Target
        };
      case 'stratégie':
        return {
          gradient: 'from-purple-400 to-indigo-500',
          bg: 'bg-purple-50',
          icon: Compass
        };
      case 'action':
        return {
          gradient: 'from-indigo-500 to-purple-600',
          bg: 'bg-indigo-50',
          icon: Zap
        };
      default:
        return {
          gradient: 'from-purple-400 to-purple-600',
          bg: 'bg-purple-50',
          icon: Target
        };
    }
  };

  const phaseStyle = getPhaseStyle();
  const PhaseIcon = phaseStyle.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header compact avec moins de padding */}
        <div className={`relative bg-gradient-to-r ${phaseStyle.gradient} p-4 text-white flex-shrink-0`}>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
              <PhaseIcon size={22} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-1">
                {analysis.welcomeMessage.title}
              </h2>
              <p className="text-white/90 text-xs truncate">
                {objective.name}
              </p>
            </div>
          </div>
        </div>

        {/* Corps du modal avec scroll interne */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          
          {/* État d'avancement - VERSION COMPACTE */}
          <div className={`${phaseStyle.bg} rounded-lg p-3 border border-current/20`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 bg-gradient-to-r ${phaseStyle.gradient} rounded-lg flex items-center justify-center text-white`}>
                  <TrendingUp size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Votre progression</h3>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{analysis.globalProgress}%</div>
                <div className="text-xs text-gray-500">Avancement</div>
              </div>
            </div>

            {/* Statistiques horizontales compactes */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white rounded-md p-2 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <CheckCircle className="w-3 h-3 text-green-600" />
                  <span className="text-xs font-medium text-gray-600">Décisions</span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {analysis.notesStats.decisions}
                </div>
              </div>
              
              <div className="bg-white rounded-md p-2 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <HelpCircle className="w-3 h-3 text-orange-600" />
                  <span className="text-xs font-medium text-gray-600">Défis</span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {analysis.notesStats.challenges}
                </div>
              </div>
              
              <div className="bg-white rounded-md p-2 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Package className="w-3 h-3 text-purple-600" />
                  <span className="text-xs font-medium text-gray-600">Sources</span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {analysis.notesStats.sources}
                </div>
              </div>
            </div>
          </div>

          {/* Alertes contextuelles - VERSION COMPACTE */}
          {analysis.alerts.length > 0 && (
            <div className="space-y-2">
              {analysis.alerts.map((alert, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-2 p-2 rounded-lg border-l-4 ${alert.color}`}
                >
                  <alert.icon className={`w-4 h-4 flex-shrink-0 ${alert.iconColor} mt-0.5`} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900">{alert.title}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{alert.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recommandations d'actions - MISE EN AVANT */}
          <div className="space-y-3">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-orange-500" />
              Prochaines étapes recommandées
            </h3>

            <div className="space-y-2">
              {analysis.recommendations.map((rec, index) => (
                <button
                  key={index}
                  onClick={() => {
                    onNavigate(rec.target);
                    onClose();
                  }}
                  className={`w-full text-left p-3 rounded-xl border-2 transition-all hover:shadow-md ${
                    index === 0
                      ? 'border-purple-300 bg-purple-50 hover:border-purple-400'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      index === 0 ? 'bg-purple-200' : 'bg-gray-100'
                    }`}>
                      <rec.icon className={`w-5 h-5 ${
                        index === 0 ? 'text-purple-700' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="font-semibold text-sm text-gray-900 truncate">{rec.title}</h4>
                        {index === 0 && (
                          <span className="text-xs px-2 py-0.5 bg-purple-600 text-white rounded-full flex-shrink-0">
                            Recommandé
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-1">{rec.description}</p>
                      {rec.reason && (
                        <p className="text-xs text-purple-600 mt-0.5 font-medium">
                          {rec.reason}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Message personnalisé - VERSION COMPACTE */}
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  {analysis.welcomeMessage.subtitle}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {analysis.welcomeMessage.message}
                </p>
              </div>
            </div>
          </div>

          {/* Accès rapide - VERSION COMPACTE */}
          <div className="border-t pt-3">
            <h3 className="text-xs font-medium text-gray-700 mb-2">
              Accès rapide à vos ressources
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {analysis.quickAccess.map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (item.available) {
                      onNavigate(item.target);
                      onClose();
                    }
                  }}
                  className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-xs ${
                    item.available
                      ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                  }`}
                  disabled={!item.available}
                >
                  <item.icon className={`w-3.5 h-3.5 ${
                    item.available ? 'text-gray-600' : 'text-gray-300'
                  }`} />
                  <span className={`flex-1 truncate ${item.available ? 'text-gray-700' : 'text-gray-400'}`}>
                    {item.label}
                  </span>
                  {item.count > 0 && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                      {item.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SmartObjectiveWelcome;